import classNames from "classnames";
import { timingFunctions } from "polished";
import * as React from "react";

import { SvgSprite, TooltipPopover, ValidationError } from "..";
import { Highlighter } from "./Highlighter";
import { ListItem, ListItemProps } from "./ListItem";
import { Menu, MenuHeader } from "./Menu";

type HtmlInputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

type HtmlDivProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

type WrapperDivProps = HtmlDivProps;

export type AsyncTypeaheadMenuProps = {
  ref: React.MutableRefObject<HTMLUListElement | HTMLDivElement | null>;
  id: string;
  role: string;
  "aria-label": string;
  className: string;
  style: React.CSSProperties;
};

export type MenuComponents<T> = {
  Menu: typeof Menu;
  MenuItem: React.FC<{
    option: T;
    position: number;
    render?: ListItemProps<T>["render"];
  }>;
  MenuHeader: typeof MenuHeader;
  CurrentHighlighter: React.FC<{
    children: string;
  }>;
};

export interface AsyncTypeaheadProps<T> {
  id: string;
  isLoading: boolean;
  isValid?: boolean;
  isInvalid?: boolean;
  labelKey?: (option: T) => string;
  minLength?: number;
  onChange?: (option: T[], event?: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onInputChange?: (
    text: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onSearch: (query: string) => void;
  onBlur?: HtmlDivProps["onBlur"];
  onFocus?: HtmlInputProps["onFocus"];
  onClick?: HtmlDivProps["onClick"];
  onKeyDown?: HtmlDivProps["onKeyDown"];
  options?: T[];
  inputProps?: Omit<HtmlInputProps, "onChange" | "onBlur" | "onFocus">;
  selected?: T[];
  autoFocus?: boolean;
  defaultInputValue?: string;
  onSearchDelay?: number;
  open?: boolean;
  wrapperProps?: WrapperDivProps;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  hideValidation?: boolean;

  renderMenu?: (
    results: T[],
    menuProps: AsyncTypeaheadMenuProps,
    menuComponents: MenuComponents<T>
  ) => JSX.Element;
}

export interface AsyncTypeaheadState<T> {
  selected: T[];
  value: string;
  query: string;
  activePosition: number;
  isFocused: boolean;
  lastInputType: InputEvent["inputType"] | null;
}

export const AsyncTypeahead = <T,>({
  id,
  isLoading,
  isValid = false,
  onInputChange,
  onSearch,
  onChange,
  onClear,
  onBlur = (e): void => void 0,
  onFocus = (e): void => void 0,
  onKeyDown = (e): void => void 0,
  options = [],
  labelKey = (n: T): string => n as string,
  minLength = 2,
  inputProps,
  selected = [],
  autoFocus = false,
  defaultInputValue = "",
  onSearchDelay = 400,
  open = undefined,
  wrapperProps,
  placeholder = "",
  error,
  touched,
  hideValidation,
  renderMenu,
}: AsyncTypeaheadProps<T>): JSX.Element => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const listBoxRef = React.useRef<HTMLDivElement | HTMLUListElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const stateRef = React.useRef<AsyncTypeaheadState<T>>({
    selected,
    value: selected.length ? labelKey(selected[0]) : defaultInputValue,
    query: selected.length ? labelKey(selected[0]) : defaultInputValue,
    activePosition: -1,
    isFocused: false,
    lastInputType: null,
  });

  const [state, $setState] = React.useState(() => {
    if (!selected.length) {
      return {
        selected: [],
        value: defaultInputValue,
        query: defaultInputValue,
        activePosition: -1,
        isFocused: false,
        lastInputType: null,
      };
    }
    return stateRef.current;
  });

  const isMenuOpen =
    typeof open === "undefined"
      ? state.isFocused && state.query.length >= minLength
      : open;

  // store these functions in ref as they might change over time
  // we don't want to recreate event handlers
  const labelKeyRef = React.useRef(labelKey);
  const onChangeRef = React.useRef(onChange);
  const onSearchRef = React.useRef(onSearch);
  const onInputChangeRef = React.useRef(onInputChange);
  const optionsRef = React.useRef(options);
  const isMenuOpenRef = React.useRef(isMenuOpen);

  const clearBtnRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    onChangeRef.current = onChange;
    onSearchRef.current = onSearch;
    onInputChangeRef.current = onInputChange;
    labelKeyRef.current = labelKey;
    optionsRef.current = options;
    isMenuOpenRef.current = isMenuOpen;
  });

  const getState = React.useCallback(() => {
    return stateRef.current;
  }, []);

  const setState: typeof $setState = React.useMemo(() => {
    return (state): void => {
      const nextState =
        typeof state === "function" ? state(stateRef.current) : state;

      stateRef.current = nextState;
      $setState(nextState);
    };
  }, []);

  const showHideClearButton = (isVisible: boolean) => {
    if (isVisible) {
      clearBtnRef.current?.setAttribute("tabindex", "0");
      clearBtnRef.current?.style.setProperty("opacity", "1");
    } else {
      clearBtnRef.current?.setAttribute("tabindex", "-1");
      clearBtnRef.current?.style.setProperty("opacity", "0");
    }
  };

  const handleChange = React.useMemo(() => {
    let timeout: NodeJS.Timeout;

    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      clearTimeout(timeout);

      const { target, nativeEvent } = event;
      const { value } = target;

      const inputType = (nativeEvent as InputEvent).inputType;

      if (onChangeRef.current) {
        onChangeRef.current([], event);
      }

      showHideClearButton(!!value);

      setState((prev) => {
        return {
          ...prev,
          selected: [],
          value,
          query: value,
          activePosition: -1,
          isFocused: true,
          lastInputType: inputType,
        };
      });

      if (onInputChangeRef.current) {
        onInputChangeRef.current(value, event);
      }

      if (value.length >= minLength) {
        timeout = setTimeout(() => {
          onSearchRef.current(value);
        }, onSearchDelay);
      }
    };
  }, [minLength, onSearchDelay, setState]);

  const handleClear = () => {
    setState((prev) => ({
      ...prev,
      selected: [],
      value: "",
      query: "",
      activePosition: -1,
      isFocused: false,
    }));

    if (onChangeRef.current) {
      onChangeRef.current([]);
    }

    if (inputRef.current) {
      inputRef.current.innerHTML = "";
      inputRef.current.focus();
    }

    showHideClearButton(false);
  };

  const handleFocus = (e: any) => {
    setState((prev) => ({
      ...prev,
      isFocused: true,
    }));
    onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    if (e.currentTarget === e.target) {
      setState((prev) => ({
        ...prev,
        isFocused: false,
        activePosition: prev.query !== prev.value ? -1 : prev.activePosition,
      }));
      onBlur(e);
    }

    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setState((prev) => ({
        ...prev,
        isFocused: false,
        activePosition: prev.query !== prev.value ? -1 : prev.activePosition,
      }));
      onBlur(e);
    }
  };

  const selectOption = React.useCallback(
    (position: number, focusMenu = false): void => {
      if (position === -1) {
        return;
      }

      const option = optionsRef.current[position];
      const inputValue = labelKeyRef.current(option);

      setState((prev) => ({
        ...prev,
        query: inputValue,
        value: inputValue,
        activePosition: position,
        isFocused: focusMenu,
      }));

      if (onChangeRef.current) {
        onChangeRef.current([option]);
      }
    },
    [setState]
  );

  /* Components Section */
  const menuProps: AsyncTypeaheadMenuProps = React.useMemo(() => {
    return {
      ref: listBoxRef,
      id: `${id}__listbox`,
      role: "listbox",
      "aria-label": "Options",
      className: "dropdown-menu d-block",
      style: {
        width: "100%",
        maxHeight: "300px",
        overflow: "auto",
        position: "absolute",
        background: "#fff",
        display: isMenuOpen ? "block" : "none",
        visibility: isMenuOpen ? "visible" : "hidden",
      },
    };
  }, [id, isMenuOpen]);

  const CurrentHighlighter = React.useMemo(() => {
    const CurrentHighlighter: React.FC<{ children: string }> = ({
      children,
    }): JSX.Element => {
      return <Highlighter filter={getState().query}>{children}</Highlighter>;
    };

    return CurrentHighlighter;
  }, [getState]);

  const MenuItem = React.useMemo(() => {
    const MenuItem: React.FC<{
      option: T;
      position: number;
      render?: ListItemProps<T>["render"];
    }> = ({ option, position, render, children }) => {
      const label = labelKeyRef.current(option);
      const listItemId = `${id}__option-${position}`;
      const active = getState().activePosition === position;
      const onClick = () => {
        selectOption(position);
        showHideClearButton(true);
      };

      return (
        <ListItem
          id={listItemId}
          active={active}
          label={label}
          onClick={onClick}
          value={option}
          listBoxRef={listBoxRef}
          render={render}
        >
          {children ?? (
            <Highlighter filter={getState().query}>{label}</Highlighter>
          )}
        </ListItem>
      );
    };

    return MenuItem;
  }, [id, selectOption, getState]);

  const menuComponents = React.useMemo(() => {
    return {
      Menu,
      MenuItem,
      MenuHeader,
      CurrentHighlighter,
    };
  }, [MenuItem, CurrentHighlighter]);

  /*  */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const { key } = e;

    switch (key) {
      case "PageUp":
        if (isMenuOpen) {
          listBoxRef.current?.scrollTo({ top: 0 });
          e.preventDefault();
        }
        break;
      case "PageDown":
        if (isMenuOpen) {
          listBoxRef.current?.scrollTo({
            top: listBoxRef.current?.scrollHeight,
          });
          e.preventDefault();
        }
        break;
      case "ArrowUp":
      case "ArrowDown": {
        if (isLoading || !isMenuOpen) {
          break;
        }

        setState((prev) => {
          let nextIdx = -1;
          if (key === "ArrowUp") {
            nextIdx =
              prev.activePosition === -1
                ? options.length - 1
                : prev.activePosition - 1;
          } else {
            nextIdx =
              prev.activePosition < options.length - 1
                ? prev.activePosition + 1
                : -1;
          }

          return {
            ...prev,
            activePosition: nextIdx,
            isFocused: true,
            value: ((): string => {
              if (!options[nextIdx]) {
                return prev.value;
              }

              if (typeof options[nextIdx] === "string") {
                return options[nextIdx] as string;
              }

              return labelKey(options[nextIdx]);
            })(),
          };
        });

        e.preventDefault();
        break;
      }

      case "Enter": {
        if (isMenuOpen) {
          e.preventDefault();
          selectOption(getState().activePosition);
        }
        break;
      }

      case "Backspace": {
        break;
      }

      case "Escape": {
        if (isMenuOpen) {
          setState((prev) => ({
            ...prev,
            // value: prev.query,
            isFocused: !prev.isFocused,
            activePosition: -1,
          }));
        }

        break;
      }

      default: {
        break;
      }
    }

    onKeyDown(e);
  };

  React.useEffect(() => {
    // options changed

    const curState = getState();
    if (
      options.length &&
      curState.lastInputType != null &&
      isMenuOpenRef.current
    ) {
      let nextActiveIdx;

      switch (curState.lastInputType) {
        case "deleteWordBackward":
        case "deleteContentBackward":
        case "deleteContent": {
          nextActiveIdx = -1;
          break;
        }
        default: {
          const queryLowerCase = curState.query.toLocaleLowerCase();
          nextActiveIdx = options.findIndex((n) =>
            labelKeyRef
              .current(n)
              .toLocaleLowerCase()
              .startsWith(queryLowerCase)
          );
        }
      }

      const nextValue =
        nextActiveIdx === -1
          ? curState.query
          : labelKeyRef.current(options[nextActiveIdx]);

      setState((prev) => ({
        ...prev,
        value: nextValue,
        activePosition: nextActiveIdx,
      }));
    }
  }, [options, getState, setState]);

  React.useEffect(() => {
    if (inputRef.current && state.activePosition !== -1) {
      const queryLower = state.query.toLowerCase();
      const valueLower = state.value.toLowerCase();

      if (valueLower.indexOf(queryLower) !== -1) {
        inputRef.current.setSelectionRange(
          state.query.length,
          state.value.length
        );
      }
    }
  }, [state.value, state.activePosition, state.query]);

  const [autoComplete, setAutoComplete] = React.useState<
    HtmlInputProps["autoComplete"]
  >(() => {
    if (inputProps?.autoComplete) {
      return inputProps.autoComplete;
    }
    return undefined;
  });

  React.useEffect(() => {
    // Focus on mount useEffect
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }

    /*
      Auto complete handling
      - set autocomplete to initialAutoComplete value from props
      - if no autoComplete prop passed through, set as undefined initially
      - when this useEffect runs, set it to "off"

      This allows chrome/any other browsers in the future to persist the inputs
      value on history changes on a server load or if js is disabled
     */
    setAutoComplete("off");
  }, [autoFocus]);

  return (
    <>
      <div
        {...wrapperProps}
        tabIndex={inputProps?.disabled ? undefined : -1}
        className={classNames(
          "position-relative d-flex flex-grow-1",
          wrapperProps?.className || ""
        )}
        ref={wrapperRef}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <input
          ref={inputRef}
          id={id}
          {...inputProps}
          role="combobox"
          aria-autocomplete="both"
          aria-expanded={isMenuOpen}
          aria-controls={`${id}__listbox`}
          aria-haspopup={true}
          aria-invalid={touched && !!error}
          aria-activedescendant={
            state.activePosition === -1
              ? ""
              : `${id}__option-${state.activePosition}`
          }
          className={classNames("form-control", inputProps?.className, {
            "is-valid": isValid,
            "is-invalid": touched && !!error,
          })}
          onChange={handleChange}
          value={state.activePosition === -1 ? state.query : state.value}
          autoCorrect="off"
          autoCapitalize="none"
          autoComplete={autoComplete}
          spellCheck={false}
          aria-describedby={[inputProps?.["aria-describedby"], `${id}__sr-desc`]
            .filter((x): x is string => !!x)
            .join(" ")}
        />
        <span className="sr-only" aria-hidden id={`${id}__sr-desc`}>
          Suggestions will appear below the field as you type
        </span>

        {((): React.ReactNode => {
          // if (!isMenuOpen) {
          //   return null;
          // }

          if (renderMenu) {
            return renderMenu(options, menuProps, menuComponents);
          }

          return (
            <Menu
              {...menuProps}
              ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            >
              {((): React.ReactNode => {
                if (isLoading) {
                  return <li className="dropdown-item disabled">Loading...</li>;
                }

                if (options.length) {
                  return options.map((option, idx) => {
                    return (
                      <MenuItem key={idx} option={option} position={idx} />
                    );
                  });
                }

                return (
                  <li className="dropdown-item disabled">No matches found.</li>
                );
              })()}
            </Menu>
          );
        })()}

        {onClear && (
          <TooltipPopover placement="top" tooltipText="Clear">
            <button
              type="button"
              ref={clearBtnRef}
              aria-label="Clear"
              tabIndex={state.query || state.value ? 0 : -1}
              className={classNames(
                "position-absolute btn text-muted bg-transparent border-0 p-0"
              )}
              css={`
                top: 0.5rem;
                bottom: 0.5rem;
                right: 0.75rem;
                transition: opacity 300ms ${timingFunctions("easeOutCubic")};
                opacity: ${state.query || state.value ? 1 : 0};
              `}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClear();
                onClear();
              }}
            >
              <span
                className="rounded-circle embed-responsive embed-responsive-1by1 p-3"
                css={`
                  transition: all 300ms ${timingFunctions("easeOutCubic")};

                  &:hover {
                    background-color: ${(p): string => p.theme.gray200};
                    box-shadow: 1px 1px 3px ${(p): string => p.theme.gray400};
                  }
                `}
              >
                <SvgSprite
                  id="icons:close"
                  width={14}
                  height={14}
                  aria-label="Clear"
                  className="img-fluid embed-responsive-item p-2"
                />
              </span>
            </button>
          </TooltipPopover>
        )}
      </div>

      {/* Do not display this on the MultiLocationTypeahead */}
      {!hideValidation && (
        <ValidationError
          errorId={`${id}-error-message`}
          className={classNames({ "d-block": !!error && touched })}
          message={!!error && touched ? error : null}
        />
      )}
    </>
  );
};
